import axios from "axios";

const workOrderUrl = "/api/workorders";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const getWorkOrders = async (companyKey, filters = {}) => {
  try {
    const response = await axios.get(workOrderUrl, {
      params: { companyKey, ...filters },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getWorkOrderById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${id}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createWorkOrder = async (workOrder) => {
  try {
    const response = await axios.post(workOrderUrl, workOrder);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateWorkOrder = async (id, workOrder) => {
  try {
    const response = await axios.put(`${workOrderUrl}/${id}`, workOrder);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Moves a work order to start at plannedStartDate, an ISO string. The server moves the end by the
 * same amount when the order has both, and changes nothing else. That is why this is not
 * updateWorkOrder: a PUT sends the whole order back and would undo whatever a colleague changed
 * since the list was fetched. Manager only.
 */
export const moveWorkOrderPlannedDates = async (id, { companyKey, plannedStartDate }) => {
  try {
    const response = await axios.patch(`${workOrderUrl}/${id}/planned-dates`, { companyKey, plannedStartDate });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteWorkOrder = async (companyKey, id) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${id}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// No userId. The server takes the actor from the token, because that name is what the status
// log carries and what a closure is signed with.
export const changeWorkOrderStatus = async (id, { companyKey, newStatus }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${id}/status`, {
      companyKey,
      newStatus,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Hands the work order to another department, another trade or both — at least one of the two
 * targets. It moves the order rather than creating a second one, so the response is the same work
 * order with a new department or trade — which is why nothing here rewrites its title or
 * description any more, and why the reason is required: it becomes the comment the receiving
 * technician reads.
 *
 * No userId. The server takes it from the session, because who handed the job over is signed on
 * that comment.
 */
export const escalateWorkOrder = async (id, { companyKey, targetDepartmentId, targetTradeId, reason }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${id}/escalate`, {
      companyKey,
      targetDepartmentId: targetDepartmentId ?? null,
      targetTradeId: targetTradeId ?? null,
      reason,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/* The work order's status changes, oldest first, with any department or trade hand-over resolved to names. */
export const getWorkOrderStatusLog = async (companyKey, id) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${id}/status-log`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/* Books time against the signed-in user. No userId: the server takes it from the token, so the
 * only time you can open or close here is your own. */
export const checkInWorkOrder = async (id, { companyKey }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${id}/checkin`, {
      companyKey,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const checkOutWorkOrder = async (id, { companyKey }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${id}/checkout`, {
      companyKey,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getWorkOrderTasks = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/tasks`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createWorkOrderTask = async (workOrderId, task) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/tasks`, task);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateWorkOrderTask = async (workOrderId, taskId, task) => {
  try {
    const response = await axios.put(`${workOrderUrl}/${workOrderId}/tasks/${taskId}`, task);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const changeWorkOrderTaskStatus = async (workOrderId, taskId, { companyKey, newStatus }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/tasks/${taskId}/status`, {
      companyKey,
      newStatus,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteWorkOrderTask = async (companyKey, workOrderId, taskId) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${workOrderId}/tasks/${taskId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getWorkOrderComments = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/comments`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// No authorUserId on any of the three. The server signs the comment with the token's user and
// compares that same user against the comment on edit and delete, so an id sent from here would
// at best be ignored and at worst be someone else's name on your words.
export const createWorkOrderComment = async (workOrderId, { companyKey, body }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/comments`, {
      companyKey,
      body,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateWorkOrderComment = async (workOrderId, commentId, { companyKey, body }) => {
  try {
    const response = await axios.put(`${workOrderUrl}/${workOrderId}/comments/${commentId}`, {
      companyKey,
      body,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteWorkOrderComment = async (companyKey, workOrderId, commentId) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${workOrderId}/comments/${commentId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getWorkOrderAttachments = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/attachments`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const uploadWorkOrderAttachment = async (workOrderId, { companyKey, file, onProgress }) => {
  try {
    const formData = new FormData();
    formData.append("companyKey", companyKey);
    formData.append("file", file);
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const downloadWorkOrderAttachment = async (companyKey, workOrderId, attachmentId, fileName) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/attachments/${attachmentId}/download`, {
      params: { companyKey },
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ?? `attachment-${attachmentId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteWorkOrderAttachment = async (companyKey, workOrderId, attachmentId) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${workOrderId}/attachments/${attachmentId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getMaterialUsage = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/materials`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createMaterialUsage = async (workOrderId, material) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/materials`, material);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateMaterialUsage = async (workOrderId, materialId, material) => {
  try {
    const response = await axios.put(`${workOrderUrl}/${workOrderId}/materials/${materialId}`, material);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteMaterialUsage = async (companyKey, workOrderId, materialId) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${workOrderId}/materials/${materialId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getLaborHours = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/labor`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateLaborHour = async (workOrderId, laborHourId, { companyKey, checkInAt, checkOutAt, notes }) => {
  try {
    const response = await axios.put(`${workOrderUrl}/${workOrderId}/labor/${laborHourId}`, {
      companyKey,
      checkInAt,
      checkOutAt,
      notes,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getRelatedDocuments = async (companyKey, workOrderId) => {
  try {
    const response = await axios.get(`${workOrderUrl}/${workOrderId}/documents`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createRelatedDocument = async (workOrderId, document) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/documents`, document);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteRelatedDocument = async (companyKey, workOrderId, linkId) => {
  try {
    const response = await axios.delete(`${workOrderUrl}/${workOrderId}/documents/${linkId}`, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// Current user takes ownership of an Open work order (Cmms_Field).
// Server resolves the assignee from the auth context — body only needs companyKey.
export const claimWorkOrder = async (workOrderId, { companyKey }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/claim`, { companyKey });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// Operator quick-report. Creates a work order on an asset node (Cmms_Field).
// Lives under /api/assets/{id}, not /api/workorders. Reporter comes from auth context.
// workOrderType must be MachineBreakdown(1) or MachineProblem(2). Photo is uploaded
// separately as an attachment after the work order is created.
export const reportIssue = async (assetNodeId, { companyKey, title, description, workOrderType }) => {
  try {
    const response = await axios.post(`/api/assets/${assetNodeId}/report-issue`, {
      companyKey,
      title,
      description,
      workOrderType,
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// Completed and closed work orders on an asset node and everything under it, most recently
// finished first, one page at a time: { items, totalCount, page, pageSize }. Under /api/assets
// like report-issue. `page` is 1-based; the server caps pageSize at 100 and echoes the size it
// actually used.
export const getAssetWorkOrderHistory = async (companyKey, assetNodeId, { page, pageSize }) => {
  try {
    const response = await axios.get(`/api/assets/${assetNodeId}/work-orders/history`, {
      params: { companyKey, page, pageSize },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// The preventive work orders on an asset node and everything under it that are not done yet —
// raised by a schedule or typed as preventive — soonest planned first. The history's other half:
// every order in the subtree is in exactly one of the two. Paged the same way as the history:
// { items, totalCount, page, pageSize }, `page` 1-based, pageSize capped at 100 by the server.
export const getAssetActivePreventiveWorkOrders = async (companyKey, assetNodeId, { page, pageSize }) => {
  try {
    const response = await axios.get(`/api/assets/${assetNodeId}/work-orders/active-preventive`, {
      params: { companyKey, page, pageSize },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// Work orders dispatched to the current user. `statuses` is an array of
// status names (e.g. ["Open", "InProgress"]) — the API parses names, not numbers.
export const getMyWorkOrders = async (companyKey, statuses) => {
  try {
    const params = { companyKey };
    if (statuses?.length) params.status = statuses.join(",");
    const response = await axios.get(`${workOrderUrl}/mine`, { params });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

// Reopen = manager-only status transition (Completed/Closed -> Open) via the status
// endpoint. NewStatus 2 = WorkOrderStatus.Open. The status payload has no reason field,
// so when a reason is given we persist it as a comment after the transition succeeds.
//
// No userId at all now: the transition and the comment that explains it are both stamped from
// the token.
export const reopenWorkOrder = async (workOrderId, { companyKey, reason }) => {
  try {
    const response = await axios.post(`${workOrderUrl}/${workOrderId}/status`, {
      companyKey,
      newStatus: 2,
    });
    if (reason?.trim()) {
      await createWorkOrderComment(workOrderId, { companyKey, body: reason.trim() });
    }
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
